import { useState, useRef, useEffect } from 'react';

/**
 * Draggable wrapper component for icons (activity buttons and notes)
 * Allows users to drag icons around the PDF page and saves positions
 */
export default function DraggableIcon({
  children,
  initialLeft,
  initialTop,
  onDragEnd,
  pageElement,
  containerElement = null,
  disabled = false,
  className = '',
  onClick = null
}) {
  const [position, setPosition] = useState({ left: initialLeft, top: initialTop });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const iconRef = useRef(null);
  const mouseDownPos = useRef({ x: 0, y: 0 });
  const onDragEndRef = useRef(onDragEnd);
  const onClickRef = useRef(onClick);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef({ left: initialLeft, top: initialTop });
  const hasMovedRef = useRef(false);
  const autoScrollInterval = useRef(null);

  // Update refs when callbacks change
  useEffect(() => {
    onDragEndRef.current = onDragEnd;
    onClickRef.current = onClick;
  }, [onDragEnd, onClick]);

  // Update position ref when position changes
  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  // Update hasMoved ref when it changes
  useEffect(() => {
    hasMovedRef.current = hasMoved;
  }, [hasMoved]);

  // Track if we just finished a drag to prevent immediate reset
  const justFinishedDrag = useRef(false);

  // Update position when initial position changes (e.g., zoom, chapter switch, or user position load)
  // Only update if not currently dragging and we haven't just finished a drag
  // This prevents the icon from snapping back immediately after dragging
  useEffect(() => {
    if (!isDragging && !justFinishedDrag.current) {
      setPosition({ left: initialLeft, top: initialTop });
      positionRef.current = { left: initialLeft, top: initialTop };
    }
    // Reset the flag after a short delay to allow position updates from API
    // Also prevents clicks for a short time after dragging
    if (justFinishedDrag.current) {
      const timer = setTimeout(() => {
        justFinishedDrag.current = false;
      }, 300); // 300ms delay - enough to prevent click events but not too long
      return () => clearTimeout(timer);
    }
  }, [initialLeft, initialTop, isDragging]);

  const handleMouseDown = (e) => {
    if (disabled) return;

    e.stopPropagation();

    if (!pageElement || !iconRef.current) return;

    // Store initial mouse position to detect if it's a click or drag
    mouseDownPos.current = { x: e.clientX, y: e.clientY };
    setHasMoved(false);

    // Calculate the offset from where the mouse clicked on the icon
    // Get the icon's bounding rect relative to viewport
    const iconRect = iconRef.current.getBoundingClientRect();
    const pageRect = pageElement.getBoundingClientRect();

    // Calculate offset from mouse position to icon center
    // This offset ensures the icon follows the cursor smoothly
    const iconCenterX = iconRect.left + iconRect.width / 2;
    const iconCenterY = iconRect.top + iconRect.height / 2;

    const offset = {
      x: e.clientX - iconCenterX,
      y: e.clientY - iconCenterY
    };

    dragStartRef.current = offset;
    setDragStart(offset);
    setIsDragging(true);
  };


  // Add global event listeners when dragging
  useEffect(() => {
    if (!isDragging) return;

    // Create handlers that use refs to avoid stale closures
    const handleGlobalMouseMove = (e) => {
      if (disabled || !pageElement || !iconRef.current) return;

      // Prevent default to avoid text selection and other behaviors during drag
      e.preventDefault();
      e.stopPropagation();

      // Check if mouse has moved significantly (more than 5px) to consider it a drag
      const moveDistance = Math.sqrt(
        Math.pow(e.clientX - mouseDownPos.current.x, 2) +
        Math.pow(e.clientY - mouseDownPos.current.y, 2)
      );

      if (moveDistance > 5) {
        hasMovedRef.current = true;
        setHasMoved(true);
      }

      const rect = pageElement.getBoundingClientRect();

      // Calculate new position: mouse position minus the offset, relative to page
      // The offset ensures the icon center follows the cursor
      let newLeft = e.clientX - dragStartRef.current.x - rect.left;
      let newTop = e.clientY - dragStartRef.current.y - rect.top;

      // NO BOUNDARY CONSTRAINTS during drag - allow free movement
      // This enables smooth cross-page dragging

      positionRef.current = { left: newLeft, top: newTop };
      setPosition({ left: newLeft, top: newTop });

      // AUTO-SCROLL: Scroll container when dragging near edges
      if (containerElement) {
        const containerRect = containerElement.getBoundingClientRect();
        const scrollSpeed = 15; // pixels per frame
        const edgeThreshold = 100; // pixels from edge to trigger scroll

        // Clear any existing scroll interval
        if (autoScrollInterval.current) {
          clearInterval(autoScrollInterval.current);
          autoScrollInterval.current = null;
        }

        // Check if near top edge
        if (e.clientY - containerRect.top < edgeThreshold) {
          autoScrollInterval.current = setInterval(() => {
            if (containerElement.scrollTop > 0) {
              containerElement.scrollTop -= scrollSpeed;
            }
          }, 16); // ~60fps
        }
        // Check if near bottom edge
        else if (containerRect.bottom - e.clientY < edgeThreshold) {
          autoScrollInterval.current = setInterval(() => {
            const maxScroll = containerElement.scrollHeight - containerElement.clientHeight;
            if (containerElement.scrollTop < maxScroll) {
              containerElement.scrollTop += scrollSpeed;
            }
          }, 16);
        }
      }
    };

    const handleGlobalMouseUp = (e) => {
      if (disabled) return;

      // Clear auto-scroll interval
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
        autoScrollInterval.current = null;
      }

      const wasDragging = hasMovedRef.current;
      setIsDragging(false);

      // If it was a drag (not just a click), save the position
      if (wasDragging && pageElement && onDragEndRef.current) {
        // Prevent the click event from firing after a drag
        e.preventDefault();
        e.stopPropagation();

        const rect = pageElement.getBoundingClientRect();
        const iconWidth = iconRef.current?.offsetWidth || 64;
        const iconHeight = iconRef.current?.offsetHeight || 64;

        // Use current position from ref
        const currentPos = positionRef.current;
        const centerX = currentPos.left + iconWidth / 2;
        const centerY = currentPos.top + iconHeight / 2;

        // Convert to percentage
        const positionLeft = (centerX / rect.width) * 100;
        const positionTop = (centerY / rect.height) * 100;

        // Mark that we just finished a drag to prevent immediate position reset and clicks
        justFinishedDrag.current = true;

        // Add a temporary click listener to prevent clicks after drag
        const preventClick = (clickEvent) => {
          // Check if the click is on this icon or its children
          if (iconRef.current && iconRef.current.contains(clickEvent.target)) {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
            clickEvent.stopImmediatePropagation();
          }
        };

        // Add listener in capture phase to catch clicks before they bubble
        document.addEventListener('click', preventClick, { capture: true, once: false });

        // Remove the listener after a short delay
        setTimeout(() => {
          document.removeEventListener('click', preventClick, { capture: true });
        }, 300);

        // Call onDragEnd with the position
        onDragEndRef.current(positionTop, positionLeft);
      } else if (!wasDragging && onClickRef.current) {
        // If it was just a click (not a drag), trigger onClick
        onClickRef.current(e);
      }

      hasMovedRef.current = false;
      setHasMoved(false);
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    // Prevent text selection while dragging
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';

      // Clean up auto-scroll interval
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
        autoScrollInterval.current = null;
      }
    };
  }, [isDragging, pageElement, disabled]);

  // Handle click events - prevent clicks if it was a drag
  // Use capture phase to catch clicks before they reach child elements
  const handleClick = (e) => {
    // If we just finished a drag or are currently dragging, prevent the click
    // This prevents the activity modal from opening after dragging
    if (justFinishedDrag.current || hasMovedRef.current || isDragging) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      // Also prevent clicks on child elements
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
      }
      return false;
    }
    // If it was a genuine click (not a drag), let it through
    // The onClick handler in handleGlobalMouseUp will handle it
  };

  // Also handle click in capture phase to catch it before child elements
  const handleClickCapture = (e) => {
    if (justFinishedDrag.current || hasMovedRef.current || isDragging) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }
  };

  return (
    <div
      ref={iconRef}
      className={`${className} ${isDragging ? 'cursor-grabbing opacity-90 z-50' : disabled ? '' : 'cursor-grab'} ${disabled ? '' : 'select-none'}`}
      style={{
        position: 'absolute',
        left: `${position.left}px`,
        top: `${position.top}px`,
        transform: 'translate(-50%, -50%)',
        transition: isDragging ? 'none' : 'none', // No transition during drag
        zIndex: isDragging ? 50 : 20,
        pointerEvents: disabled ? 'none' : 'auto'
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onClickCapture={handleClickCapture}
    >
      {children}
    </div>
  );
}

